/**
 * Home visit patient result table (訪問予定表 患者毎 — results).
 * Displays patient list with checkbox selection, pagination.
 */

import { AppCheckbox, AppPagination, AppTable, AppTooltip } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type { ColumnsType } from 'antd/es/table';
import React, { useCallback, useMemo } from 'react';
import type { HomeVisitPatientPagination, HomeVisitPatientRow } from '../types';

interface Props {
  data: HomeVisitPatientRow[];
  pagination: HomeVisitPatientPagination | null;
  selectedIds: Set<number>;
  onSelectChange: (ids: Set<number>) => void;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  hasSearched: boolean;
  serviceType: string;
}

const HomeVisitPatientTable: React.FC<Props> = ({
  data,
  pagination,
  selectedIds,
  onSelectChange,
  onPageChange,
  isLoading,
  hasSearched,
  serviceType,
}) => {
  const isA2 = serviceType === SERVICE_TYPE_A2;
  const massagerColumnTitle = isA2 ? 'はり・きゅう師名' : 'マッサージ師名';

  const allChecked = useMemo(
    () => data.length > 0 && data.every(r => selectedIds.has(r.patient_id)),
    [data, selectedIds]
  );

  const handleToggleRow = useCallback(
    (patientId: number, checked: boolean) => {
      const next = new Set(selectedIds);
      if (checked) {
        next.add(patientId);
      } else {
        next.delete(patientId);
      }
      onSelectChange(next);
    },
    [selectedIds, onSelectChange]
  );

  const handleTogglePageAll = useCallback(
    (checked: boolean) => {
      const next = new Set(selectedIds);
      for (const row of data) {
        if (checked) {
          next.add(row.patient_id);
        } else {
          next.delete(row.patient_id);
        }
      }
      onSelectChange(next);
    },
    [selectedIds, data, onSelectChange]
  );

  const columns: ColumnsType<HomeVisitPatientRow> = useMemo(
    () => [
      {
        title: (
          <AppCheckbox checked={allChecked} onChange={e => handleTogglePageAll(e.target.checked)} />
        ),
        dataIndex: 'patient_id',
        key: 'checkbox',
        width: 56,
        align: 'center',
        render: (_: unknown, record: HomeVisitPatientRow) => (
          <AppCheckbox
            checked={selectedIds.has(record.patient_id)}
            onChange={e => handleToggleRow(record.patient_id, e.target.checked)}
          />
        ),
      },
      {
        title: '受診者番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 130,
        render: (_: unknown, record: HomeVisitPatientRow) => (
          <PatientEditLink
            patientId={record.patient_id}
            value={record.serial_number}
          />
        ),
      },
      {
        title: '受診者名',
        dataIndex: 'name',
        key: 'name',
        width: 200,
        className: 'min-w-0',
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: massagerColumnTitle,
        dataIndex: 'massager_names',
        key: 'massager_names',
        className: 'min-w-0',
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
    ],
    [
      allChecked,
      selectedIds,
      massagerColumnTitle,
      handleToggleRow,
      handleTogglePageAll,
    ]
  );

  return (
    <div>
      <AppTable
        columns={columns}
        dataSource={data}
        /* The query groups by (patient_id, serial_number, name, name_kana), so use
           the same stable tuple as the React key. patient_id alone can collide when
           one patient yields multiple grouped rows. */
        rowKey={record =>
          `${record.patient_id}-${record.serial_number}-${record.name}-${record.name_kana}`
        }
        loading={isLoading}
        pagination={false}
        hasFilters={hasSearched}
        tableLayout="fixed"
      />

      {pagination && pagination.last_page > 1 && (
        <div className="flex justify-center mt-3">
          <AppPagination
            current={pagination.current_page}
            total={pagination.total}
            pageSize={pagination.per_page}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default HomeVisitPatientTable;
